import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Autocomplete,
  Switch,
  FormControlLabel,
  Typography,
  Box
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import userService, { User } from '../services/userService';
import spopService from '../services/spopService';

interface SPOP {
  id: string;
  name: string;
  location: string;
}

interface UserFormDialogProps {
  open: boolean;
  onClose: () => void;
  user?: User | null;
}

const schema = yup.object({
  username: yup.string().required('Username is required').min(3, 'Username must be at least 3 characters'),
  email: yup.string().required('Email is required').email('Email must be valid'),
  name: yup.string().required('Name is required').min(2, 'Name must be at least 2 characters'),
  password: yup.string().when('$isEdit', {
    is: false,
    then: (schema) => schema.required('Password is required')
      .min(6, 'Password must be at least 6 characters')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one lowercase letter, one uppercase letter, and one number'
      ),
    otherwise: (schema) => schema.optional()
      .min(6, 'Password must be at least 6 characters')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one lowercase letter, one uppercase letter, and one number'
      )
  }),
  role: yup.string().required('Role is required').oneOf(['admin', 'operator', 'viewer'], 'Invalid role'),
  spop_id: yup.string().nullable().when('role', {
    is: 'operator',
    then: (schema) => schema.required('SPOP is required for operators'),
    otherwise: (schema) => schema.nullable()
  }),
  status: yup.string().required().oneOf(['active', 'inactive'], 'Invalid status')
});

export const UserFormDialog: React.FC<UserFormDialogProps> = ({ open, onClose, user }) => {
  const queryClient = useQueryClient();
  const isEdit = !!user;

  const { data: spopsData, isLoading: spopsLoading } = useQuery({
    queryKey: ['spops'],
    queryFn: () => spopService.getAllSPOPs(),
    enabled: open
  });

  const spops = spopsData?.data?.data?.spops || [];

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    context: { isEdit },
    defaultValues: {
      username: '',
      email: '',
      name: '',
      password: '',
      role: 'viewer' as const,
      spop_id: null,
      status: 'active' as const
    }
  });

  const selectedRole = watch('role');

  useEffect(() => {
    if (user) {
      reset({
        username: user.username,
        email: user.email,
        name: user.name || '',
        password: '',
        role: user.role,
        spop_id: user.spop_id || null,
        status: user.status || 'active'
      });
    } else {
      reset({
        username: '',
        email: '',
        name: '',
        password: '',
        role: 'viewer',
        spop_id: null,
        status: 'active'
      });
    }
  }, [user, reset]);

  const mutation = useMutation({
    mutationFn: (data: any) => {
      const payload = { ...data };
      if (isEdit && !payload.password) {
        delete payload.password;
      }
      
      return isEdit 
        ? userService.updateUser(user!.id!, payload)
        : userService.createUser(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
      reset();
    }
  });

  const onSubmit = (data: any) => {
    console.log('Form data being submitted:', data);
    mutation.mutate(data);
  };

  const handleClose = () => {
    onClose();
    reset();
    mutation.reset();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        {isEdit ? 'Edit User' : 'Create New User'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {mutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {(mutation.error as any)?.response?.data?.message || 'An error occurred'}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Full Name"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Username"
                    fullWidth
                    error={!!errors.username}
                    helperText={errors.username?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    type="email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={isEdit ? "New Password (leave empty to keep current)" : "Password"}
                    type="password"
                    fullWidth
                    error={!!errors.password}
                    helperText={
                      errors.password?.message || 
                      (!isEdit ? "Must contain uppercase, lowercase, and number" : "")
                    }
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.role}>
                    <InputLabel>Role</InputLabel>
                    <Select {...field} label="Role">
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="operator">Operator</MenuItem>
                      <MenuItem value="viewer">Viewer</MenuItem>
                    </Select>
                    {errors.role && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.role.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {selectedRole === 'operator' && (
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="spop_id"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <Autocomplete
                      {...field}
                      options={spops}
                      getOptionLabel={(option: SPOP) => `${option.name} - ${option.location}`}
                      value={spops.find((spop: SPOP) => spop.id === value) || null}
                      onChange={(_, newValue) => onChange(newValue?.id || null)}
                      loading={spopsLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="SPOP Assignment"
                          error={!!errors.spop_id}
                          helperText={errors.spop_id?.message}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {spopsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value === 'active'}
                          onChange={(e) => field.onChange(e.target.checked ? 'active' : 'inactive')}
                        />
                      }
                      label="Active User"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {field.value === 'active' ? 'User can log in and access the system' : 'User account is disabled'}
                    </Typography>
                  </Box>
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={20} /> : null}
          >
            {isEdit ? 'Update' : 'Create'} User
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
